/* eslint-disable @typescript-eslint/naming-convention */
import LZString from 'lz-string';

import {
  DriveInfo,
  DriveSensorInfo,
  OrgFilterItem,
  RawDriveSensorInfo,
  RawDriveInfo,
} from '../types';

const convertUTCStringToDate = (dateStr: string): Date =>
  new Date(`${dateStr}Z`);

export const publicationUuidToURL = (uuid: string): string =>
  `${window.location.origin}/share/${uuid}`;

const transformSensorInfos = (
  sensors: RawDriveSensorInfo[]
): DriveSensorInfo[] =>
  /* 
    TODO: (vivian)
    Sensor is sorted by its serial number so the multi-sensor data can be handled gracefully as single sensor data. This step is not needed after we fully implemented the multi-sensor viz
  */
  sensors
    .map((sensor) => ({
      sdkFwBranch: sensor.sdk_fw_branch,
      sdkFwBuildDate: sensor.sdk_fw_build_date
        ? convertUTCStringToDate(sensor.sdk_fw_build_date)
        : null,
      sdkFwBuildRev: sensor.sdk_fw_build_rev,
      sdkFwCommitHash: sensor.sdk_fw_commit_hash,
      sdkFwImageRev: sensor.sdk_fw_image_rev,
      sdkFwMergeRequest: sensor.sdk_fw_merge_request,
      sdkFwVersionCore: sensor.sdk_fw_version_core,
      sdkFwVersionPrerelease: sensor.sdk_fw_version_prerelease,
      sdkLidarMode: sensor.sdk_lidar_mode,
      sdkProductBeamSpacing: sensor.sdk_prod_beam_spacing,
      sdkProductGeneration: sensor.sdk_prod_generation,
      sdkProductHwRevision: sensor.sdk_prod_hw_rev,
      sdkProductLine: sensor.sdk_prod_line,
      sdkProductPartNumber: sensor.sdk_prod_pn,
      sdkProductResolution: sensor.sdk_prod_resolution,
      sdkProductSerialNumber: sensor.sdk_prod_sn,
      sdkProductType: sensor.sdk_prod_type,
    }))
    .sort((a, b) => {
      const snA = a.sdkProductSerialNumber?.toString() || '';
      const snB = b.sdkProductSerialNumber?.toString() || '';
      if (snA === snB) {
        return 0;
      }
      if (!snA) {
        return 1; // If snA is undefined, place it after snB
      }
      if (!snB) {
        return -1; // If snB is undefined, place it after snA
      }
      return snA.localeCompare(snB);
    });

/* 
  This method is used for viz page and on list page, we need less recording info.
  The transform method for list page has been moved to component lib, leave this one here for viz page usage
*/
export const transformRawDrives: (raw: RawDriveInfo) => DriveInfo = (raw) => {
  const startTime = raw.start_time ? new Date(`${raw.start_time}Z`) : '...';
  const stopTime = raw.stop_time ? new Date(`${raw.stop_time}Z`) : '...';

  return {
    id: raw.id,
    dataType: raw.data_type,
    organizationId: raw.organization_id,
    startTime,
    stopTime,
    uploadStatus: raw.upload_status,
    dataCategory: raw.data_category,
    sourceBucket: raw.source_bucket,
    sourcePrefix: raw.source_prefix,
    processedAt: convertUTCStringToDate(raw.processed_at),
    osfGenerationStatus: raw.osf_generation_status,
    osfGenerationDate: raw.osf_generation_date
      ? convertUTCStringToDate(raw.osf_generation_date)
      : null,
    osfBucket: raw.osf_bucket,
    osfPrefix: raw.osf_prefix,
    slamGenerationStatus: raw.slam_generation_status,
    slamGenerationDate: raw.slam_generation_date
      ? convertUTCStringToDate(raw.slam_generation_date)
      : null,
    slamBucket: raw.slam_bucket,
    slamPrefix: raw.slam_prefix,
    uploadedBy: raw.uploaded_by,
    uploadedById: raw.uploaded_by_id,
    publicTitle: raw.public_title,
    publicURL: raw.publication_uuid
      ? publicationUuidToURL(raw.publication_uuid)
      : undefined,
    sensorInfos: transformSensorInfos(raw.sensor_info),
    tags: raw.tags,
    visualization_status: raw.visualization_status,
    organizationName: raw.organization_name,
    recordingTitle: raw.recording_title,
  };
};

export const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return `${date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })} | ${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`;
};

export const convertFilterArrayToObject = (
  data: OrgFilterItem[]
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  data.forEach((item) => {
    result[item.field_name] = item.values
      .filter((value) => value !== null)
      .map((value) => String(value));
  });

  return result;
};

export const parseUrlParams = (search: string): Record<string, string[]> => {
  const params = new URLSearchParams(search);
  const compressedFilters = params.get('filters');

  const filterObject: Record<string, string[]> = {};

  // Convert old-style filters to new format
  params.forEach((value, key) => {
    if (key !== 'filters' && key !== 'order_by' && key !== 'sort_order') {
      if (filterObject[key]) {
        filterObject[key].push(value);
      } else {
        filterObject[key] = [value];
      }
    }
  });

  // If compressed filters exist, add them to the filterObject
  if (compressedFilters) {
    try {
      const decompressed =
        LZString.decompressFromEncodedURIComponent(compressedFilters);
      if (decompressed) {
        const existingFilters = JSON.parse(decompressed) as Record<
          string,
          string[]
        >;
        // Merge with existing filters
        Object.keys(existingFilters).forEach((key) => {
          if (filterObject[key]) {
            filterObject[key] = [
              ...new Set([...filterObject[key], ...existingFilters[key]]),
            ];
          } else {
            filterObject[key] = existingFilters[key];
          }
        });
      }
    } catch (error) {
      console.error('Error decompressing filters:', error);
    }
  }

  return filterObject;
};

/**
 * Converts a single DriveSensorInfo object to RawDriveSensorInfo format
 */
export function driveSensorInfoToRaw(
  sensorInfo: DriveSensorInfo
): RawDriveSensorInfo {
  return {
    sdk_fw_branch: sensorInfo.sdkFwBranch,
    sdk_fw_build_date: sensorInfo.sdkFwBuildDate?.toISOString() || null,
    sdk_fw_build_rev: sensorInfo.sdkFwBuildRev,
    sdk_fw_commit_hash: sensorInfo.sdkFwCommitHash,
    sdk_fw_image_rev: sensorInfo.sdkFwImageRev,
    sdk_fw_merge_request: sensorInfo.sdkFwMergeRequest,
    sdk_fw_version_core: sensorInfo.sdkFwVersionCore,
    sdk_fw_version_prerelease: sensorInfo.sdkFwVersionPrerelease,
    sdk_lidar_mode: sensorInfo.sdkLidarMode,
    sdk_prod_beam_spacing: sensorInfo.sdkProductBeamSpacing,
    sdk_prod_generation: sensorInfo.sdkProductGeneration,
    sdk_prod_hw_rev: sensorInfo.sdkProductHwRevision,
    sdk_prod_line: sensorInfo.sdkProductLine,
    sdk_prod_pn: sensorInfo.sdkProductPartNumber,
    sdk_prod_resolution: sensorInfo.sdkProductResolution,
    sdk_prod_sn: sensorInfo.sdkProductSerialNumber,
    sdk_prod_type: sensorInfo.sdkProductType,
  };
}

/**
 * Converts an array of DriveSensorInfo objects to RawDriveSensorInfo format
 */
export function driveSensorInfoArrayToRaw(
  sensorInfoArray: DriveSensorInfo[]
): RawDriveSensorInfo[] {
  return sensorInfoArray.map(driveSensorInfoToRaw);
}
